import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';

/**
 * Durable offline cache for React Query, backed by IndexedDB.
 *
 * Why IndexedDB rather than localStorage:
 *  - It behaves identically in the Capacitor WebView (iOS/Android) and on the
 *    web, so the same persistence works across every target.
 *  - It is asynchronous, so serializing a large query cache never blocks the
 *    main thread / rendering.
 *  - It has no ~5MB string quota. The legislative data this app caches (bills,
 *    notes, calendars, legislators) would overflow localStorage quickly.
 *
 * `idb-keyval` is a tiny wrapper over a single IndexedDB object store. We only
 * need get/set/del here, so a full client-side database (Dexie) would be
 * overkill — TanStack Query owns the cache shape, not us.
 */

// Namespaced so other IndexedDB users in this origin don't collide with us.
// The cache is additionally version-busted via `buster` in the provider config
// (see lib/offline/queryClient.ts) so a schema change can invalidate it.
const OFFLINE_CACHE_KEY = 'capitol-commons-rq-cache';

export const indexedDbPersister = createAsyncStoragePersister({
  key: OFFLINE_CACHE_KEY,
  // createAsyncStoragePersister serializes the dehydrated client to a string
  // (JSON.stringify by default) and hands us that string to store; idb-keyval
  // persists it verbatim and returns it untouched on read.
  storage: {
    // idb-keyval resolves missing keys to `undefined`; the persister interface
    // expects `null`, so normalize.
    getItem: (key) => get<string>(key).then((value) => value ?? null),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
  // Coalesce bursts of cache writes (e.g. many queries settling at once) into at
  // most one IndexedDB write per second.
  throttleTime: 1000,
});
